/*
 * Long text field component
 */

import React from 'react';
import Base from './base.jsx';
import { 
  Mode,
  castDataType,
  DataType,
} from '../common.jsx';
import Helpers from './helpers.jsx'
import Setting from "./setting.jsx";


import RichTextEditor from '../../common/RichTextEditor.jsx';

export default class LongTextField extends Base {

        static get CATEGORY() {
            return 'question';
        }

        static get TYPE() {
            return 'long_text_field';
        }

        static icon(){
            return <i className="fa fa-align-left"></i>;
        }

        static get TRANSLATED_ATTRIBUTES() {
            return {
                label: 'field_default_label',
                placeholder: 'field_default_placeholder',
                description: 'field_default_description',
                help: null
            };
        }

        static defaultContent(language) {
            return Object.assign(super.defaultContent(language), {
                label: 'label',
                placeholder: 'placeholder',
                description: 'description',
                name: 'field_' + Helpers.randomString(),
                reportKey: '',
                help: 'help',
                required: false,
                label_hidden: false,
                wordLimit: 0,
                lockEditWhenPrefilled: false,
                prettyView: {
                    hide: false,
                    show_label: false
                }
            });
        }

        constructor(props) {
            super(props);
            this.textareaId = Helpers.randomString(16); // Used in rich text editor as unique id
        }

        renderView() {
            let validationFailures = this.getFormValidationFailures();

            if (this.props.content.useRichTextEditor && !this.props.pdf) {
              return (<div>

                {this.getLockedInformationTooltip(this.modeFillOrView() && this.isReadOnlyPrefilledAnswer(), <>
                    <label className={this.props.content.label_hidden ? 'hidden-label': ''}>
                        <span className="field-label">{this.getText('label')}</span>
                    {this.props.content.required ? <span className='required-field'> *</span> : null}
                    {this.helpIcon()}
                    {this.renderPrefillIcon()}
                    </label>
                    <RichTextEditor
                        id={this.textareaId}
                        fieldname={this.fullFieldName()}
                        wordLimit={this.props.content.wordLimit}
                        readOnly={this.props.mode != Mode.FILL || this.isReadOnlyAnswer()}
                        value={this.getAnswer()}
                        placeholder={this.getText('placeholder')}
                        updateAnswer={(val) => this.answerChanged(val)}
                        preview={this.props.mode == Mode.EDIT}
                        className={this.getInputClassNames()}
                    />
                    {this.descriptionText()}
                    {!this.modeFillOrView() && this.isReadOnlyAnswer() ? this.lockedInformationDefault() : ''}
                </>)}
              </div>
            );
            }

            return (
                <div>
                    {this.getLockedInformationTooltip(this.modeFillOrView() && this.isReadOnlyPrefilledAnswer(), <>
                        <label className={this.props.content.label_hidden ? 'hidden-label': ''}>
                            <span className="field-label">{this.getText('label')}</span>
                            {this.props.content.required ? <span className='required-field'> *</span> : null}
                            {this.helpIcon()}
                            {this.renderPrefillIcon()}
                            <textarea
                                ref="textarea"
                                key={'FORM-COMP- '+  Math.random()} // Refresh key on render for updated defaultValue
                                className={validationFailures.length > 0 ? `form-control has-error warning-tooltip ${this.isReadOnlyAnswer() ? 'locked' : ''}` :  `${this.isReadOnlyAnswer() ? 'locked' : ''} form-control ${this.getInputClassNames()}`}
                                name={this.fullFieldName()}
                                defaultValue={this.getAnswer()}
                                placeholder={this.getText('placeholder')}
                                title={validationFailures.length > 0 ? validationFailures.join('\n') : ''}
                                data-validation={validationFailures.length > 0}
                                readOnly={this.props.mode != Mode.FILL || this.isReadOnlyAnswer()}
                                onBlur={(e) => this.answerChanged(e.target.value)}>
                            </textarea>
                        </label>
                        {this.descriptionText()}
                        {!this.modeFillOrView() && this.isReadOnlyAnswer() ? this.lockedInformationDefault() : ''}
                    </>)}
                </div>
            );
        }

        renderPrettyView() {
            const { hide = false, show_label = false } = this.props.content.prettyView || {};
            return (
                (!this.props.content.containsSensitiveInformation && !hide) &&
                <>
                    {show_label &&
                        <label>
                            <span className="field-label">{this.getText('label')}</span>
                        </label>
                    }
                    <div className='d-flex' style={{flexDirection: 'column'}}>
                        {(this.props.content.useRichTextEditor && !this.props.pdf) ? 
                            <div dangerouslySetInnerHTML={{__html: Helpers.linkURLs(this.getAnswer())}}></div> :
                            <span>{this.getAnswer()}</span>
                        }
                    </div>
                </>
              )
        }

        answerChanged = (value) => {
            this.updateAnswer(value);
            if (this.props.answersChangedCallback){
                this.props.answersChangedCallback();
            }
        }

        renderSettings() {
            let lockEditWhenPrefilled = this.props.content.lockEditWhenPrefilled ? true : false;
            let containsSensitiveInformation = this.props.content.containsSensitiveInformation ? true : false;

            return (
                <div>
                    <Setting.Checkbox name="required" label={I18n.t('field_required', {scope: 'fe'})} value={this.props.content.required} onChange={this.updateSettings} />
                    <Setting.TextField name="label" label={I18n.t('field_label', {scope: 'fe'})} value={this.getText('label')} onChange={this.updateSettings} />
                    <Setting.Checkbox name="label_hidden" className="label-hidden-check" label={I18n.t('field_label_hide', {scope: 'fe'})} value={this.props.content.label_hidden} onChange={this.updateSettings} />

                    {!this.props.settings['hidePlaceholderField'] &&
                      <Setting.TextField name="placeholder" label={I18n.t('field_placeholder', {scope: 'fe'})} value={this.getText('placeholder')} onChange={this.updateSettings} />
                    }
                    {!this.props.settings['hideDescriptionField'] &&
                      <Setting.LongTextField name="description" label={I18n.t('field_description', {scope: 'fe'})} value={this.getText('description')} onChange={this.updateSettings} />
                    }
                    {!this.props.settings['hideHelpTextField'] &&
                      <Setting.LongTextField name="help" label={I18n.t('field_help', {scope: 'fe'})} value={this.getText('help')} onChange={this.updateSettings} />
                    }
                    <Setting.TextField name="wordLimit" label={I18n.t('field_word_limit', {scope: 'fe'})} value={this.props.content.wordLimit} onChange={this.updateSettings} description={I18n.t('field_word_limit_description', {scope: 'fe'})} />
                    <Setting.TextField
                      name="reportKey"
                      label={I18n.t('field_report_key', {scope: 'fe'})}
                      value={this.props.content.reportKey}
                      description={I18n.t('field_report_key_description', {scope: 'fe'})}
                      {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
                    />
                    <Setting.Checkbox name="lockEdit" label={I18n.t('field_lock_edit', {scope: 'fe'})} description={I18n.t('field_lock_edit_description', {scope: 'fe'})} value={this.props.content.lockEdit} onChange={this.updateSettings} />
                    {(!this.props.settings['hideLockEditWhenPrefilled'] && AppConfig.features.form_prefill) &&
                      <Setting.Checkbox name="lockEditWhenPrefilled" label={I18n.t('field_lock_edit_when_prefilled', {scope: 'fe'})} description={I18n.t('field_lock_edit_when_prefilled_description', {scope: 'fe'})} value={this.props.content.lockEditWhenPrefilled} onChange={this.updateSettings} />
                    }
                    {!this.props.settings['hideContainsSensitiveInformation'] &&
                      <Setting.Checkbox
                        name="containsSensitiveInformation"
                        label={I18n.t('field_contains_sensitive_information', {scope: 'fe'})}
                        description={I18n.t('field_contains_sensitive_information_description', {scope: 'fe'})}
                        value={this.props.content.containsSensitiveInformation}
                        {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
                      />
                    }
                    <Setting.Checkbox name="useRichTextEditor" label={I18n.t('field_use_rich_text_editor', {scope: 'fe'})} description={I18n.t('field_use_rich_text_editor_description', {scope: 'fe'})} value={this.props.content.useRichTextEditor} onChange={this.updateSettings} />
                    {this.renderPrettyViewSettings()}
                </div>
            );
        }

        componentDidMount() {
            super.componentDidMount();
            this.componentDidUpdate();

            if (this.refs.textarea) {
                // Scale textarea height to content height but only max 400px.
                jQuery(this.refs.textarea).on('input propertychange', (e) => {
										jQuery(e.currentTarget).closest('div').attr('style', 'height: ' + e.currentTarget.scrollHeight +'px')
										jQuery(e.currentTarget).css('height', 'auto');
										let height = e.currentTarget.scrollHeight > 400 ? 400 : e.currentTarget.scrollHeight;
										jQuery(e.currentTarget).closest('div').removeAttr('style');
                    // Dealing with Textarea Height(if scrollHeight == 0) => min-height + lines x line-height + border
                    let newHeight = (height == 0) ? 34 + e.target.value.split(/\r?\n|\r/).length * 20 + 2 : height;
                    jQuery(e.currentTarget).outerHeight(newHeight + 10);
								}).trigger('input'); 
            }
        }

        componentDidUpdate() {
            super.componentDidUpdate();
            
            if (this.refs.textarea) {
                if (this.props.content.wordLimit && this.props.mode != Mode.VIEW) {
                    new Helpers.TextareaCounter(this.refs.textarea, {
                        type: 'word',
                        limit: this.props.content.wordLimit
                    });
                } else {
                    Helpers.TextareaCounter.remove(this.refs.textarea);
                }
            }
        }

        beforeUpdateSettings(content) {
            content.required = castDataType(content.required, DataType.BOOLEAN);
            content.label_hidden = castDataType(content.label_hidden, DataType.BOOLEAN);
            content.wordLimit = castDataType(content.wordLimit, DataType.INTEGER);
            content.lockEditWhenPrefilled = castDataType(content.lockEditWhenPrefilled, DataType.BOOLEAN);
            content.lockEdit = castDataType(content.lockEdit, DataType.BOOLEAN);
            content.useRichTextEditor = castDataType(content.useRichTextEditor, DataType.BOOLEAN);
            this.beforeUpdateSettingsPrettyView(content);
        }

        getFormattedPrefillAnswer() {
            return "";
        }
}
